import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { getTenant, isTenantSurface } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const PLATFORM_PATHS = ["/platform", "/landing", "/signup"];

const DEFAULT_PRIMARY = "#1E293B";
const DEFAULT_ACCENT = "#D4A017"; // Corporate Gold — Aetria signature

const BrandingContext = createContext({ branding: null, noTenant: false, resolved: false, refresh: () => {} });
export const useBranding = () => useContext(BrandingContext);

export const logoSrc = () => `${API}/api/tenant/branding/logo?tenant=${getTenant()}`;

/* ---- contrast-aware accent derivation (WCAG-safe on slate AND alabaster) ---- */
const hexToRgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
const lum = (h) => {
  const [r, g, b] = hexToRgb(h).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const contrast = (a, b) => {
  const [hi, lo] = [lum(a), lum(b)].sort((m, n) => n - m);
  return (hi + 0.05) / (lo + 0.05);
};
const mix = (h, target, ratio) => {
  const a = hexToRgb(h), b = hexToRgb(target);
  return `#${a.map((v, i) =>
    Math.round(v + (b[i] - v) * ratio).toString(16).padStart(2, "0")).join("")}`;
};
const deriveAccent = (accent, primary) => {
  let ink = accent, guard = 0;
  while (contrast(ink, "#ffffff") < 4.5 && guard++ < 24) ink = mix(ink, "#000000", 0.06);
  let bright = accent; guard = 0;
  while (contrast(bright, primary) < 3 && guard++ < 24) bright = mix(bright, "#ffffff", 0.06);
  const fg = contrast("#ffffff", accent) >= 3 ? "#ffffff" : "#0f172a";
  return { ink, bright, fg };
};

/* Applies the palette to CSS vars instantly (no reload). Exported so the
   quick palette switcher can flip colors optimistically before persisting. */
export const applyBrandVars = (primary = DEFAULT_PRIMARY, accent = DEFAULT_ACCENT, preset = "consulting") => {
  const root = document.documentElement;
  const { ink, bright, fg } = deriveAccent(accent, primary);
  root.style.setProperty("--brand-primary", primary);
  root.style.setProperty("--brand-accent", accent);
  root.style.setProperty("--brand-accent-ink", ink);
  root.style.setProperty("--brand-accent-bright", bright);
  root.style.setProperty("--brand-accent-fg", fg);
  document.body.dataset.preset = preset || "consulting";
  const minimal = preset === "minimal";
  root.style.setProperty("--sidebar-bg", minimal ? "#ffffff" : primary);
  root.style.setProperty("--sidebar-fg", minimal ? "#0f172a" : "#ffffff");
  root.style.setProperty("--sidebar-muted", minimal ? "#64748b" : "#94a3b8");
  root.style.setProperty("--sidebar-hover", minimal ? "rgba(15,23,42,0.05)" : "rgba(255,255,255,0.06)");
  root.style.setProperty("--sidebar-border", minimal ? "rgba(15,23,42,0.10)" : "rgba(255,255,255,0.10)");
};

const setFavicon = (b, primary, accent) => {
  try {
    let href;
    if (b?.has_logo) {
      href = logoSrc();
    } else {
      const c = document.createElement("canvas");
      c.width = c.height = 64;
      const ctx = c.getContext("2d");
      ctx.fillStyle = primary; ctx.fillRect(0, 0, 64, 64);
      ctx.fillStyle = accent; ctx.fillRect(0, 56, 64, 8);
      ctx.fillStyle = "#ffffff"; ctx.font = "bold 36px Inter, sans-serif";
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText(((b?.product_name || "A")[0] || "A").toUpperCase(), 32, 30);
      href = c.toDataURL("image/png");
    }
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
    link.href = href;
  } catch {}
};

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(null);
  const [noTenant, setNoTenant] = useState(false);
  const [resolved, setResolved] = useState(false); // branding bootstrap settled

  const refresh = async () => {
    const tenant = getTenant();
    if (!tenant && !isTenantSurface()) {
      // Base domain, no workspace resolved — neutral Aetria OS branding.
      // NEVER falls back to any tenant (no DEFAULT_TENANT dependency).
      setBranding(null);
      setNoTenant(false);
      setResolved(true);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/api/tenant/branding`, {
        headers: tenant ? { "X-Tenant": tenant } : {},
      });
      setBranding(data);
      setNoTenant(false);
    } catch (e) {
      if (e.response?.data?.code === "tenant_not_found") setNoTenant(true);
      // suspended or network: leave defaults; suspended screen handles UX
    } finally {
      setResolved(true);
    }
  };
  useEffect(() => { refresh(); }, []); // eslint-disable-line

  useEffect(() => {
    const platformSurface = PLATFORM_PATHS.some((p) => window.location.pathname.startsWith(p));
    const b = !platformSurface && branding ? branding : null;
    const primary = b?.primary_color || DEFAULT_PRIMARY;
    const accent = b?.accent_color || DEFAULT_ACCENT;
    applyBrandVars(primary, accent, b?.preset || "consulting");
    if (b?.product_name) document.title = b.product_name;
    setFavicon(b, primary, accent);
  }, [branding]);

  return (
    <BrandingContext.Provider value={{ branding, noTenant, resolved, refresh }}>
      {children}
    </BrandingContext.Provider>
  );
}
